import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import { toast } from "sonner";
import { Save, ArrowLeft, Upload, Trash2, FileText, Search } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

export default function AdminInsightEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAdmin();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get(`/admin/insights/${id}`).then(r => setForm(r.data)).catch(() => { toast.error("Not found"); navigate("/admin/insights"); });
  }, [id]);

  const uploadFile = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fileUrl = `${API}/files/${res.data.path}`;
      if (type === "thumbnail") {
        setForm(s => ({ ...s, thumbnail_url: fileUrl }));
      } else {
        setForm(s => ({ ...s, documents: [...(s.documents || []), { name: file.name, url: fileUrl, path: res.data.path }] }));
      }
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const removeDoc = (idx) => setForm(s => ({ ...s, documents: s.documents.filter((_, i) => i !== idx) }));

  const addTag = () => {
    if (tagsInput.trim()) {
      setForm(s => ({ ...s, tags: [...(s.tags || []), tagsInput.trim()] }));
      setTagsInput("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id: _, ...data } = form;
      await api.put(`/admin/insights/${id}`, data);
      toast.success("Saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-slate-400">Loading...</p>;

  return (
    <div data-testid="admin-insight-edit">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/admin/insights")} className="flex items-center gap-1 text-slate-500 text-sm hover:text-[#0A1628]">
          <ArrowLeft size={14} /> Back to Insights
        </button>
        <div className="flex items-center gap-3">
          <select value={form.status} onChange={e => setForm(s => ({...s, status: e.target.value}))} className="border border-slate-200 text-xs px-3 py-2 rounded-[2px]" data-testid="insight-status-select">
            <option value="draft">Draft</option>
            <option value="preview">Preview</option>
            <option value="published">Published</option>
          </select>
          {form.status === "preview" && (
            <a href={`/insights/preview/${id}`} target="_blank" rel="noopener" className="text-xs text-blue-500 border border-blue-200 px-3 py-2 rounded-[2px] hover:bg-blue-50">Preview</a>
          )}
          <button onClick={save} disabled={saving} data-testid="save-insight" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px] disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-slate-200 p-6 rounded-[2px]">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div><label className="text-xs text-slate-400 block mb-1">Title</label><input type="text" value={form.title} onChange={e => setForm(s => ({...s, title: e.target.value}))} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px] focus:border-[#0A1628] focus:outline-none" data-testid="insight-title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs text-slate-400 block mb-1">Category</label>
                <select value={form.category} onChange={e => setForm(s => ({...s, category: e.target.value}))} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px]" data-testid="insight-category">
                  <option value="Article">Article</option><option value="Judgment">Judgment</option><option value="Checklist">Checklist</option><option value="Update">Update</option>
                </select>
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">Date</label><input type="date" value={form.published_date} onChange={e => setForm(s => ({...s, published_date: e.target.value}))} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px]" /></div>
            </div>
            <div><label className="text-xs text-slate-400 block mb-1">Author</label><input type="text" value={form.author || ""} onChange={e => setForm(s => ({...s, author: e.target.value}))} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs text-slate-400 block mb-1">Excerpt / Summary</label><textarea value={form.excerpt || ""} onChange={e => setForm(s => ({...s, excerpt: e.target.value}))} rows={3} className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px]" data-testid="insight-excerpt" /></div>
          </div>
        </div>

        {/* SEO Settings */}
        <div className="bg-white border border-slate-200 p-6 rounded-[2px]">
          <div className="flex items-center gap-2 mb-4">
            <Search size={14} className="text-[#C5A059]" />
            <h3 className="text-xs uppercase tracking-wider text-slate-400">SEO Settings</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Meta Title</label>
              <input
                type="text"
                data-testid="insight-meta-title"
                value={form.meta_title || ""}
                onChange={e => setForm(s => ({...s, meta_title: e.target.value}))}
                placeholder={form.title || "Insight title for search engines"}
                className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px] focus:border-[#0A1628] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Meta Description</label>
              <input
                type="text"
                data-testid="insight-meta-description"
                value={form.meta_description || ""}
                onChange={e => setForm(s => ({...s, meta_description: e.target.value}))}
                placeholder="Brief description for search engines (155 chars)"
                className="w-full border border-slate-200 px-4 py-3 text-sm rounded-[2px] focus:border-[#0A1628] focus:outline-none"
              />
            </div>
          </div>
          <p className="text-[10px] text-slate-300 mt-2">Leave blank to auto-generate from title and excerpt.</p>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-[2px]">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Tags</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {(form.tags || []).map((t, i) => (
              <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-[2px] flex items-center gap-1">{t} <button onClick={() => setForm(s => ({...s, tags: s.tags.filter((_, j) => j !== i)}))} className="text-red-400">&times;</button></span>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="text" value={tagsInput} onChange={e => setTagsInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag" className="flex-1 border border-slate-200 px-3 py-2 text-sm rounded-[2px]" />
            <button onClick={addTag} className="text-xs bg-slate-100 px-3 py-2 rounded-[2px] hover:bg-slate-200">Add</button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-[2px]">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Content</h3>
          <div className="quill-admin">
            <ReactQuill
              theme="snow"
              value={form.content || ""}
              onChange={val => setForm(s => ({...s, content: val}))}
              modules={quillModules}
              data-testid="insight-content-editor"
            />
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-[2px]">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Thumbnail Image</h3>
          <div className="flex items-center gap-4">
            {form.thumbnail_url && <img src={form.thumbnail_url} alt="Thumbnail" className="w-24 h-16 object-cover rounded-[2px] border" />}
            <label className="flex items-center gap-2 text-xs border border-slate-200 px-4 py-2 rounded-[2px] cursor-pointer hover:border-[#0A1628]">
              <Upload size={14} /> Upload Thumbnail
              <input type="file" accept="image/*" onChange={e => uploadFile(e, "thumbnail")} className="hidden" />
            </label>
            <input type="text" value={form.thumbnail_url || ""} onChange={e => setForm(s => ({...s, thumbnail_url: e.target.value}))} placeholder="Or paste URL" className="flex-1 border border-slate-200 px-3 py-2 text-sm rounded-[2px]" />
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6 rounded-[2px]">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Documents (PDFs for download)</h3>
          {(form.documents || []).length > 0 && (
            <div className="space-y-2 mb-4">
              {form.documents.map((doc, i) => (
                <div key={i} className="flex items-center justify-between border border-slate-100 p-3 rounded-[2px]">
                  <div className="flex items-center gap-2"><FileText size={14} className="text-[#C5A059]" /><a href={doc.url} target="_blank" rel="noopener" className="text-sm text-[#0A1628] hover:text-[#C5A059]">{doc.name}</a></div>
                  <button onClick={() => removeDoc(i)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 text-xs border border-slate-200 px-4 py-2 rounded-[2px] cursor-pointer hover:border-[#0A1628] w-fit">
            <Upload size={14} /> {uploading ? "Uploading..." : "Upload Document"}
            <input type="file" accept=".pdf,.doc,.docx" onChange={e => uploadFile(e, "document")} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}
